import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../app/AppContext';
import { useStore } from '../../app/StoreContext';
import { callFn, getRefreshToken, isGuest, linkGuest, loginPassword, register, requestPasswordReset, resetPassword, resendVerification, verifyEmail } from '../../lib/api';

type Mode = 'signin' | 'register' | 'verify' | 'forgot' | 'reset';

export function Login() {
  const { slug, store } = useStore();
  const { reload, toast } = useApp();
  const nav = useNavigate();
  const location = useLocation();
  const navState = (location.state as { returnTo?: string; email?: string } | null);
  const returnTo = navState?.returnTo || `/${slug}`;
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState(navState?.email || '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : 'Something went wrong.'); }
    finally { setBusy(false); }
  };

  // "Login to order": bind this account to the store as a customer when it has
  // no store yet. Idempotent for existing members; an account that belongs to
  // another store is left alone (the cart page explains the mismatch).
  const joinHere = async () => {
    try { await callFn('join_store', { slug }); }
    catch (e) { if (!/another store/i.test(e instanceof Error ? e.message : '')) throw e; }
  };

  // Guest → account: the guest's refresh token proves this browser owns the
  // guest, so its orders are merged into the account being signed in to.
  const guestRefresh = () => (isGuest() ? getRefreshToken() : null);
  const linkPreviousGuest = async (rt: string | null) => {
    if (!rt) return;
    try { if (await linkGuest(rt)) toast('Your guest orders were added to your account.', 'ok'); }
    catch { /* best effort — the account still signs in */ }
  };

  const signIn = () => run(async () => {
    const rt = guestRefresh();
    await loginPassword(email.trim(), password);
    await linkPreviousGuest(rt);
    await joinHere();
    await reload();
    nav(returnTo, { replace: true });
  });
  const doRegister = () => run(async () => {
    await register(email.trim(), password, name.trim() || undefined);
    setMode('verify');
    toast('We emailed you a 6-digit code.', 'info');
  });
  const doVerify = () => run(async () => {
    const rt = guestRefresh();
    await verifyEmail(email.trim(), code.trim());
    await loginPassword(email.trim(), password);
    await linkPreviousGuest(rt);
    await joinHere();
    await reload();
    nav(returnTo, { replace: true });
  });
  const doForgot = () => run(async () => {
    await requestPasswordReset(email.trim());
    setMode('reset');
    toast('If that email has an account, a reset code is on its way.', 'info');
  });
  const doReset = () => run(async () => {
    await resetPassword(email.trim(), code.trim(), password);
    toast('Password updated — sign in.', 'ok');
    setMode('signin'); setPassword('');
  });

  if (!store) return null;
  const title = { signin: 'Sign in', register: 'Create account', verify: 'Check your email', forgot: 'Forgot password', reset: 'Set a new password' }[mode];

  return (
    <div className="page-narrow auth">
      <h1>{title}</h1>
      <p className="muted">{mode === 'signin' ? `Use your ${store.displayName} account.` : mode === 'verify' ? `Enter the code we sent to ${email}.` : ''}</p>
      <form onSubmit={(e) => { e.preventDefault(); ({ signin: signIn, register: doRegister, verify: doVerify, forgot: doForgot, reset: doReset })[mode](); }}>
        {mode !== 'verify' && mode !== 'reset' && (
          <label>Email<input className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        )}
        {mode === 'register' && (
          <label>Name (optional)<input className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} /></label>
        )}
        {(mode === 'signin' || mode === 'register' || mode === 'reset') && (
          <label>{mode === 'reset' ? 'New password' : 'Password'}<input className="input" type="password" autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        )}
        {(mode === 'verify' || mode === 'reset') && (
          <label>6-digit code<input className="input" inputMode="numeric" pattern="[0-9]{6}" required value={code} onChange={(e) => setCode(e.target.value)} /></label>
        )}
        {error && <p className="error">{error}</p>}
        <button className="btn btn-block" type="submit" disabled={busy}>{busy ? 'Please wait…' : title}</button>
      </form>
      <div className="auth-links">
        {mode === 'signin' && <><button className="link" onClick={() => setMode('register')}>Create an account</button><button className="link" onClick={() => setMode('forgot')}>Forgot password</button></>}
        {mode === 'register' && <button className="link" onClick={() => setMode('signin')}>Already have an account? Sign in</button>}
        {mode === 'verify' && <><button className="link" onClick={() => void resendVerification(email.trim()).then(() => toast('Code re-sent.', 'info'))}>Resend code</button><button className="link" onClick={() => setMode('signin')}>Back to sign in</button></>}
        {(mode === 'forgot' || mode === 'reset') && <button className="link" onClick={() => setMode('signin')}>Back to sign in</button>}
      </div>
      <p className="muted small"><Link to={`/${slug}`}>Back to {store.displayName}</Link></p>
    </div>
  );
}
